import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Fuel,
  UtensilsCrossed,
  Building2,
  ShieldCheck,
  Bot,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  FileText,
  Presentation,
  Smartphone,
  Sparkles,
  TrendingUp,
  Receipt,
  Layers,
} from 'lucide-react';

interface VerticalTab {
  id: string;
  name: string;
  badge: string;
  icon: React.ElementType;
  headline: string;
  subhead: string;
  problem: string;
  solution: string;
  keyFeatures: string[];
  metrics: { label: string; value: string }[];
  uiMock: {
    type: string;
    title: string;
    status: string;
    dataPoints: { label: string; value: string; highlight?: boolean; status?: 'good' | 'warn' | 'info' }[];
    footerNote: string;
  };
}

const VERTICALS: VerticalTab[] = [
  {
    id: 'fuel',
    name: 'Fuel Retail & Petrol Pumps',
    badge: 'BPCL IoTHOS + Nozzle Telemetry',
    icon: Fuel,
    headline: 'Autonomous Fuel Station Intelligence & Nozzle-Level Reconciliation',
    subhead: 'From physical tank dip to automated Tally vouchers — zero leakage, zero paperwork.',
    problem: 'Shift managers manually record meter readings, dip measurements, cash, and fleet credit in notebooks. Evaporation, pump calibration drift, and cash discrepancies only surface weeks later during CA reconciliation.',
    solution: 'Forty2 connects directly with BPCL IoTHOS telemetry and automation controllers, automatically matching nozzle-by-nozzle MPD sales against tank dips, density tests, and payment swipe batches in real time.',
    keyFeatures: [
      'Direct BPCL IoTHOS IoT telemetry & automated MPD nozzle meter sync',
      'Shift-wise 4-way balancing: Meter sales vs. physical dip, cash drawer & EDC swipes',
      'Continuous density, temperature, and evaporation loss monitoring',
      'Automated fleet & credit-customer statement generation with WhatsApp reminders',
    ],
    metrics: [
      { label: 'Reconciliation Time', value: '< 2 Mins' },
      { label: 'Shift Leakage Caught', value: '100%' },
      { label: 'Manual Ledger Entry', value: '0 Hours' },
    ],
    uiMock: {
      type: 'FUEL_STATION_TELEMETRY',
      title: 'Station Nozzle & Shift Audit — Kumar Energy (BPCL RO #256634)',
      status: 'LIVE TELEMETRY ACTIVE',
      dataPoints: [
        { label: 'MS (Petrol) Dispensed', value: '4,820.4 L', status: 'good' },
        { label: 'HSD (Diesel) Dispensed', value: '9,140.8 L', status: 'good' },
        { label: 'Dip Reading Variance', value: '+3.2 L (0.02%)', status: 'good' },
        { label: 'PineLabs / EDC Settled', value: '₹ 8,42,500', status: 'info' },
        { label: 'Credit Slips Pending', value: '₹ 1,18,200', status: 'warn' },
      ],
      footerNote: 'Auto-balanced across 12 nozzles. Tally Daybook sales voucher generated.',
    },
  },
  {
    id: 'fnb',
    name: 'Restaurants & Cloud Kitchens',
    badge: '3-Way Aggregator & EDC Recon',
    icon: UtensilsCrossed,
    headline: 'Eliminate Swiggy, Zomato & Card Payment Settlement Leakage',
    subhead: 'Reconcile millions in food aggregator payouts and credit card swipes down to every single order.',
    problem: 'Aggregators deduct commissions, delivery subsidies, cancellations, and customer discounts before remitting payouts. Spotting missing settlements, wrong commission slabs, or uncredited card swipes manually is nearly impossible.',
    solution: 'Forty2 performs automated 3-way reconciliation across POS kitchen orders, Swiggy/Zomato payout sheets, and actual bank credits. Instantly flags uncredited settlements, deduction errors, and PineLabs batch mismatches.',
    keyFeatures: [
      'Automated 3-way recon: POS Bill vs. Aggregator Statement vs. Bank Deposit',
      'PineLabs EDC & payment gateway batch settlement verification',
      'Detection of wrongful order cancellations and unauthorized commission hikes',
      'Daily food cost (COGS) and recipe margin monitoring against Tally inventory',
    ],
    metrics: [
      { label: 'Settlement Accuracy', value: '99.98%' },
      { label: 'Discrepancy Catch Rate', value: '100%' },
      { label: 'Monthly Recovery Avg.', value: '₹ 45k+' },
    ],
    uiMock: {
      type: 'FNB_RECON_ENGINE',
      title: 'Aggregator & EDC Settlement Radar — Multi-Outlet',
      status: 'BATCH RECONCILED',
      dataPoints: [
        { label: 'POS Gross Billed', value: '₹ 3,42,800', status: 'good' },
        { label: 'Swiggy Payout Verified', value: '₹ 1,84,120', status: 'good' },
        { label: 'Zomato Disputed Deductions', value: '₹ 6,420 (Flagged)', status: 'warn' },
        { label: 'PineLabs Bank Credit Match', value: '₹ 94,800 (Matched)', status: 'good' },
      ],
      footerNote: 'Disputed deductions packet formatted for immediate aggregator claim submission.',
    },
  },
  {
    id: 'compliance',
    name: 'CA Audit & Statutory Shield',
    badge: 'Form 3CD + MSME 43B(h) Engine',
    icon: ShieldCheck,
    headline: 'Automated 44AB Tax Audit Clauses & MSME 45-Day Payment Sentinel',
    subhead: 'Continuous compliance tracking that safeguards founders and tax auditors against costly disallowances.',
    problem: 'Under Section 43B(h), overdue payments to registered MSMEs beyond 45/15 days are disallowed from business expenses and taxed. Tax audit clauses (Section 40A(3) cash limits, 269ST, TDS) are usually verified only at year-end when penalties are irreversible.',
    solution: 'Forty2 runs an active compliance rules engine directly on your accounting data. Flags payments approaching the MSME 45-day threshold, verifies GST 2B ITC credits, and generates tax audit working papers with full voucher drill-down.',
    keyFeatures: [
      'MSME 43B(h) Countdown Sentinel: Live alerts for invoices nearing the 15/45-day limit',
      'Section 40A(3) Cash Limit Alert: Real-time flag for daily cash expenses exceeding ₹10,000',
      'GST 2B vs Purchase Auto-Match: Detects uncredited ITC and non-filing vendors',
      'Auditor Sampling Workbench: One-click export of 44AB tax audit working papers',
    ],
    metrics: [
      { label: '43B(h) Tax Disallowance', value: '₹ 0 Risk' },
      { label: 'Lost ITC Recovered', value: 'Up to 4%' },
      { label: 'Audit Scrutiny Prep', value: 'Instant' },
    ],
    uiMock: {
      type: 'AUDIT_ASSURANCE_ENGINE',
      title: 'Statutory Compliance & Clause Sentinel — FY 2025-26',
      status: 'SENTINEL ACTIVE',
      dataPoints: [
        { label: 'MSME 43B(h) Due in ≤ 5 Days', value: '3 Vendors (₹ 1.82L)', status: 'warn' },
        { label: 'Sec 40A(3) Cash Violations', value: '0 Detected', status: 'good' },
        { label: 'GSTR-2B Unclaimed ITC', value: '₹ 74,320 Matched', status: 'good' },
        { label: 'TDS Deduction Compliance', value: '100% On-Track', status: 'good' },
      ],
      footerNote: 'All vouchers tagged with immutable audit trail and supporting documentation.',
    },
  },
  {
    id: 'ingestion',
    name: 'Zero-Touch Autonomous Ingestion',
    badge: 'WhatsApp + Voice + Inbound Mail',
    icon: Bot,
    headline: 'Capture Vouchers via WhatsApp Voice Notes, Bills & Inbound Email',
    subhead: 'No data entry bottlenecks. Speak, snap, or forward — AI structures and stages vouchers for Tally.',
    problem: 'Founders and branch managers pile up paper receipts, petrol bills, and supplier PDFs. Staff spend hundreds of hours keying data into Tally, causing backlogs, typos, and delayed monthly closings.',
    solution: 'Send a voice note on WhatsApp ("Paid ₹2,400 for office stationery via UPI"), forward a vendor invoice to your dedicated Forty2 inbox, or drop proprietary Excel sheets. AI parses line items, maps ledgers, and queues vouchers for instant approval.',
    keyFeatures: [
      'WhatsApp & Voice Note Logging: Natural speech parsed into ledger-mapped entries',
      'Dedicated Inbound Email (`invoices@company.forty2.ai`): Auto-extracts PDF attachments',
      'Visual No-Code Excel & Bank Statement Mapper: Drag-and-drop mapping for any column format',
      'Real-time, zero-lag TallyPrime background synchronization',
    ],
    metrics: [
      { label: 'Data Entry Reduction', value: '85%' },
      { label: 'Voucher Turnaround', value: '< 30 Sec' },
      { label: 'Line-Item Accuracy', value: '99.4%' },
    ],
    uiMock: {
      type: 'INGESTION_STAGING',
      title: 'Autonomous Ingestion Pipeline — Staged for Tally Approval',
      status: '3 VOUCHERS PROCESSED',
      dataPoints: [
        { label: 'WhatsApp Voice Note', value: '₹ 4,500 Machine Repair → Staged', status: 'good' },
        { label: 'Email PDF: HPCL Tanker #921', value: '₹ 14,20,000 → PO Matched', status: 'good' },
        { label: 'HDFC Bank Statement (CSV)', value: '142 Rows Reconciled', status: 'good' },
        { label: 'Ledger Mapping Confidence', value: '99.6% Automated', status: 'info' },
      ],
      footerNote: 'Sync agent push ready. Auto-creates missing master ledgers on confirmation.',
    },
  },
  {
    id: 'board-mis',
    name: 'Executive Board MIS & AI CFO',
    badge: '1-Click PowerPoint (.pptx) Decks',
    icon: Presentation,
    headline: 'Publication-Grade Board Decks & Natural Language Financial Intelligence',
    subhead: 'Transform raw accounting ledgers into executive presentations and interactive decision intelligence.',
    problem: 'Compiling investor decks and board MIS packs takes finance teams 3 to 5 days every month copying charts into PowerPoint and Excel. When leadership asks ad-hoc questions, answers take days.',
    solution: 'Forty2 exports comprehensive, branded PowerPoint (`.pptx`) board presentations in one click. Plus, an embedded Conversational AI CFO answers questions about margins, debtors, and burn rates in seconds.',
    keyFeatures: [
      '1-Click Export of board-ready PowerPoint (.pptx) presentations with charts & ratios',
      'Conversational AI CFO: Ask "Who owes us over 60 days?" or "Why did gross margin dip?"',
      'Automated WhatsApp & Email Statement of Accounts (SOA) with debtor reminders',
      'Multi-Company Consolidation: Single-pane-of-glass across subsidiaries and sister concerns',
    ],
    metrics: [
      { label: 'Board Deck Prep', value: '1 Click' },
      { label: 'DSO Reduction', value: '12 Days Avg.' },
      { label: 'Decision Velocity', value: 'Real-time' },
    ],
    uiMock: {
      type: 'BOARD_MIS_DECK',
      title: 'Executive Board Deck — FY26 Q2 Performance Overview',
      status: 'PPTX EXPORT READY',
      dataPoints: [
        { label: 'Operating Revenue', value: '₹ 4.82 Cr (+18.4% YoY)', status: 'good' },
        { label: 'EBITDA Margin', value: '22.8% (Target 21.0%)', status: 'good' },
        { label: 'Cash Runway Remaining', value: '14.2 Months', status: 'info' },
        { label: 'Debtor Collection Period (DSO)', value: '38 Days (-9 Days)', status: 'good' },
      ],
      footerNote: 'Includes full slide breakdown: P&L, Working Capital, Ratio Matrix & CapEx Analysis.',
    },
  },
];

export const IndustryShowcase: React.FC = () => {
  const [activeTabId, setActiveTabId] = useState<string>('fuel');
  const currentTab = VERTICALS.find((v) => v.id === activeTabId) || VERTICALS[0];
  const IconComponent = currentTab.icon;

  return (
    <div
      style={{
        borderRadius: '16px',
        backgroundColor: 'var(--bg-card)',
        border: '1px solid var(--border-subtle)',
        boxShadow: 'var(--shadow-panel)',
        overflow: 'hidden',
      }}
    >
      {/* Top Tab Bar */}
      <div
        style={{
          display: 'flex',
          overflowX: 'auto',
          borderBottom: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-surface)',
          padding: '6px',
          gap: '6px',
          scrollbarWidth: 'none',
        }}
      >
        {VERTICALS.map((v) => {
          const isActive = v.id === activeTabId;
          const TabIcon = v.icon;
          return (
            <button
              key={v.id}
              onClick={() => setActiveTabId(v.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '12px 18px',
                borderRadius: '10px',
                border: 'none',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s ease',
                background: isActive ? 'var(--brand-red)' : 'transparent',
                color: isActive ? '#ffffff' : 'var(--text-secondary)',
                fontWeight: isActive ? 700 : 500,
                fontSize: '0.9rem',
              }}
            >
              <TabIcon size={16} />
              <span>{v.name}</span>
            </button>
          );
        })}
      </div>

      {/* Main Content Area */}
      <div style={{ padding: '36px 32px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: '36px',
            alignItems: 'start',
          }}
        >
          {/* Left Column: Solution Detail */}
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <span className="badge-pill badge-red" style={{ fontSize: '0.78rem' }}>
                <Sparkles size={12} />
                {currentTab.badge}
              </span>
            </div>

            <h3
              style={{
                fontSize: 'clamp(1.5rem, 2.5vw, 1.95rem)',
                fontWeight: 800,
                color: 'var(--text-primary)',
                lineHeight: '1.22',
                marginBottom: '14px',
                letterSpacing: '-0.02em',
              }}
            >
              {currentTab.headline}
            </h3>

            <p
              style={{
                fontSize: '1.02rem',
                color: 'var(--text-secondary)',
                lineHeight: '1.6',
                marginBottom: '24px',
              }}
            >
              {currentTab.subhead}
            </p>

            {/* Problem vs Solution Callout Box */}
            <div
              style={{
                borderRadius: '12px',
                backgroundColor: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                padding: '20px',
                marginBottom: '24px',
              }}
            >
              <div style={{ marginBottom: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <AlertCircle size={15} color="var(--brand-red-light)" />
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--brand-red-light)' }}>
                    The Problem Today
                  </span>
                </div>
                <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                  {currentTab.problem}
                </p>
              </div>

              <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <CheckCircle2 size={15} color="var(--signal-green)" />
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--signal-green)' }}>
                    The Forty2 Advantage
                  </span>
                </div>
                <p style={{ fontSize: '0.88rem', color: 'var(--text-primary)', lineHeight: '1.5' }}>
                  {currentTab.solution}
                </p>
              </div>
            </div>

            {/* Feature Checklist */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
              {currentTab.keyFeatures.map((feat, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <CheckCircle2 size={16} color="var(--brand-red)" style={{ flexShrink: 0, marginTop: '3px' }} />
                  <span style={{ fontSize: '0.92rem', color: 'var(--text-secondary)', lineHeight: '1.45' }}>
                    {feat}
                  </span>
                </div>
              ))}
            </div>

            {/* Metrics Bar */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px',
                paddingTop: '20px',
                borderTop: '1px solid var(--border-subtle)',
              }}
            >
              {currentTab.metrics.map((m, idx) => (
                <div key={idx}>
                  <div className="font-mono" style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                    {m.value}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Simulated Live UI Terminal Card */}
          <div
            style={{
              borderRadius: '14px',
              backgroundColor: 'var(--bg-deep)',
              border: '1px solid var(--border-medium)',
              boxShadow: 'var(--shadow-elevated)',
              overflow: 'hidden',
            }}
          >
            {/* Header Terminal bar */}
            <div
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderBottom: '1px solid var(--border-subtle)',
                padding: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ display: 'flex', gap: '6px' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ef4444' }} />
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#f59e0b' }} />
                  <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#10b981' }} />
                </div>
                <span className="font-mono" style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  forty2://live-engine/{currentTab.id}
                </span>
              </div>
              <span
                className="font-mono"
                style={{
                  fontSize: '0.7rem',
                  padding: '3px 8px',
                  borderRadius: '6px',
                  backgroundColor: 'rgba(0, 230, 118, 0.15)',
                  color: 'var(--signal-green)',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                }}
              >
                {currentTab.uiMock.status}
              </span>
            </div>

            {/* Inner Dashboard View */}
            <div style={{ padding: '24px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                {currentTab.id === 'fuel' ? (
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      backgroundColor: '#ffffff',
                      border: '1px solid var(--border-medium)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '2px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
                    }}
                  >
                    <img
                      src="/kumar-energy-logo-256.png"
                      alt="Kumar Energy"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                  </div>
                ) : (
                  <div
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      backgroundColor: 'var(--brand-red-glow)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--brand-red)',
                    }}
                  >
                    <IconComponent size={20} />
                  </div>
                )}
                <div>
                  <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {currentTab.uiMock.title}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Autonomous Financial Operating Layer • Powered by Forty2
                  </div>
                </div>
              </div>

              {/* Data points table */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                {currentTab.uiMock.dataPoints.map((dp, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 14px',
                      borderRadius: '8px',
                      backgroundColor: 'var(--bg-surface)',
                      border: '1px solid var(--border-subtle)',
                    }}
                  >
                    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      {dp.label}
                    </span>
                    <span
                      className="font-mono"
                      style={{
                        fontSize: '0.9rem',
                        fontWeight: 700,
                        color:
                          dp.status === 'warn'
                            ? 'var(--brand-red-light)'
                            : dp.status === 'good'
                            ? 'var(--signal-green)'
                            : 'var(--text-primary)',
                      }}
                    >
                      {dp.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Terminal Footer Note */}
              <div
                style={{
                  padding: '12px 14px',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(229, 57, 53, 0.08)',
                  border: '1px solid var(--border-red)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <CheckCircle2 size={14} color="var(--brand-red)" style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                  {currentTab.uiMock.footerNote}
                </span>
              </div>
            </div>

            {/* Bottom Terminal Action Bar */}
            <div
              style={{
                padding: '14px 20px',
                backgroundColor: 'var(--bg-surface)',
                borderTop: '1px solid var(--border-subtle)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Ready to deploy for your company
              </span>
              <Link
                to="/diagnostic"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: 'var(--brand-red)',
                  textDecoration: 'none',
                }}
              >
                <span>Check Your Readiness</span>
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

